import Vue from "vue";
import Vuex from "vuex";

Vue.use(Vuex);

import { path } from "../api";
import * as actions from "../utils/actions";
import {
  restClient,
  restClientAll,
  restClientWithOutAuthentication,
} from "../utils/restClient";

// import modules
import layout from "./modules/layout";
import adminAuth from "./modules/adminAuth";
import clientAuth from "./modules/clientAuth";
import admins from "./modules/admins";
import adminProfiles from "./modules/adminProfiles";
import users from "./modules/users";
import lotteries from "./modules/lotteries";
import raffles from "./modules/raffles";
import blocks from "./modules/blocks";
import cart from "./modules/cart";
import orders from "./modules/orders";
import transactions from "./modules/transactions";
import systemConfig from "./modules/systemConfig";
import commissions from "./modules/commissions";
import buy from "./modules/buy";
import commissionProfiles from "./modules/commisionProfiles";
import transactionProfiles from "./modules/transactionProfiles";
import autoApprovalRules from "./modules/autoApprovalRules";
import reports from "./modules/reports";
import shortUrl from "./modules/shortUrl";
import notifications from "./modules/notifications";
import promotions from "./modules/promotions";
import depositAccountGroups from "./modules/depositAccountGroups";
import operationGroups from "./modules/operationGroups";
import promotionRaffles from "./modules/promotionRaffles";
import scheduledPromotionRaffles from "./modules/scheduledPromotionRaffles";
import ads from "./modules/ads";
import workingHours from "./modules/workingHours";
import jackpot from "./modules/jackpot";
import identityVerification from "./modules/identityVerification";
import emailTemplate from "./modules/emailTemplate";
import redisQuotas from "./modules/redisQuotas";
import subAccounts from "./modules/subAccounts";
import birthdayPromotions from "./modules/birthdayPromotions";
import rankingPoints from "./modules/rankingPoints";
import tips from "./modules/tips";

const store = new Vuex.Store({
  state: {
    redirectRoute: null,
    redirectAfterAll: null,
    provinces: [],
    countries: [],
    loading: true,
  },
  mutations: {
    [actions.GET_PROVINCES](state, payload = []) {
      state.provinces = payload;
    },
    [actions.GET_COUNTRY_LIST](state, payload = []) {
      state.countries = payload;
    },
    [actions.SET_LOADING](state, payload = false) {
      state.loading = payload;
    },
  },
  actions: {
    async fetchProvinces({ commit }, payload = {}) {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async (resolve, reject) => {
        try {
          const response = await restClient(
            path.admin.provinces.base,
            "GET",
            null,
            payload
          );
          commit(actions.GET_PROVINCES, response.data.data);
          resolve(response);
        } catch (error) {
          commit(actions.GET_PROVINCES, []);
          reject(error);
        }
      });
    },
    async fetchProvincesSelect({ commit }, payload = {}) {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async (resolve, reject) => {
        try {
          const response = await restClientWithOutAuthentication(
            path.client.provinces.base,
            "GET",
            null,
            payload
          );
          commit(actions.GET_PROVINCES, response.data.data);
          resolve(response);
        } catch (error) {
          commit(actions.GET_PROVINCES, []);
          reject(error);
        }
      });
    },
    async fetchCountries({ commit }, payload = {}) {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async (resolve, reject) => {
        try {
          const response = await restClientAll(
            path.client.countries.base,
            "GET",
            null,
            payload
          );
          commit(actions.GET_COUNTRY_LIST, response.data.data);
          resolve(response);
        } catch (error) {
          commit(actions.GET_COUNTRY_LIST, []);
          reject(error);
        }
      });
    },
    async sendContactUsEmail(_, payload = {}) {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async (resolve, reject) => {
        try {
          const response = await restClient(
            path.client.contact,
            "POST",
            payload
          );
          resolve(response);
        } catch (error) {
          reject(error);
        }
      });
    },
    async sendProblemReportEmail(_, payload = {}) {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async (resolve, reject) => {
        try {
          const response = await restClient(
            path.client.problemReport,
            "POST",
            payload,
            {},
            { "Content-Type": "multipart/form-data" }
          );
          resolve(response);
        } catch (error) {
          reject(error);
        }
      });
    },
    async fetchAccountByPhone(_, payload) {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async (resolve, reject) => {
        try {
          const response = await restClient(
            path.client.accountByPhone(payload),
            "GET"
          );
          resolve(response);
        } catch (error) {
          reject(error);
        }
      });
    },
    setLoading({ commit }, payload = false) {
      commit(actions.SET_LOADING, payload);
    },
    getFileUrl(_, payload) {
      return path.client.files.single(payload);
    },
    async fetchValidateOriginRequest() {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async (resolve, reject) => {
        try {
          const response = await restClientWithOutAuthentication(
            path.client.vor,
            "GET"
          );
          resolve(response);
        } catch (error) {
          reject(error);
        }
      });
    },
    async fetchBigWinners() {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async (resolve, reject) => {
        try {
          const response = await restClientWithOutAuthentication(
            path.client.bigWinners,
            "GET"
          );
          resolve(response);
        } catch (error) {
          reject(error);
        }
      });
    },
  },
  modules: {
    layout,
    adminAuth,
    clientAuth,
    admins,
    users,
    lotteries,
    raffles,
    blocks,
    cart,
    orders,
    transactions,
    systemConfig,
    commissions,
    buy,
    commissionProfiles,
    transactionProfiles,
    autoApprovalRules,
    reports,
    shortUrl,
    notifications,
    promotions,
    depositAccountGroups,
    operationGroups,
    promotionRaffles,
    scheduledPromotionRaffles,
    ads,
    workingHours,
    jackpot,
    identityVerification,
    emailTemplate,
    redisQuotas,
    subAccounts,
    birthdayPromotions,
    rankingPoints,
    adminProfiles,
    tips,
  },
  getters: {
    defaultCountry: (state) => state.countries.find((item) => item.is_default),
    getCountries: (state) => state.countries,
  },
});

export default store;
